import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { IconButton } from '@doculegis/shared'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CustomDataTag } from '@/components/common/CustomDataTag'
import { useCustomJurisdictions } from '@/hooks/useCustomData'
import { DEFAULT_JURISDICTIONS } from '@/lib/constants'

interface JurisdictionSelectProps {
  value: string
  onChange: (value: string) => void
}

export function JurisdictionSelect({ value, onChange }: JurisdictionSelectProps) {
  const { savedJurisdictions, addJurisdiction, removeJurisdiction } = useCustomJurisdictions()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newJurisdiction, setNewJurisdiction] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const defaultJurisdictions = DEFAULT_JURISDICTIONS.filter(j => j !== 'Other')
  const allJurisdictions = useMemo(
    () => [...new Set([...defaultJurisdictions, ...savedJurisdictions, 'Other'])],
    [defaultJurisdictions, savedJurisdictions]
  )
  const filteredJurisdictions = useMemo(
    () => allJurisdictions.filter(j =>
      j.toLowerCase().includes(searchValue.toLowerCase())
    ),
    [allJurisdictions, searchValue]
  )

  useEffect(() => {
    if (!isDropdownOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const handleAdd = () => {
    if (newJurisdiction.trim()) {
      const success = addJurisdiction(newJurisdiction.trim())
      if (success) {
        onChange(newJurisdiction.trim())
        setNewJurisdiction('')
        setShowAddForm(false)
      }
    }
  }

  const handleRemove = (jurisdiction: string) => {
    removeJurisdiction(jurisdiction)
    if (value === jurisdiction) {
      onChange('US (Delaware)')
    }
  }

  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
        Governing Law
      </label>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(prev => !prev)}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
            {value || 'Select jurisdiction'}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </button>

        {isDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-[rgba(15,23,42,0.09)] bg-white p-2 text-[#101828] shadow-[var(--shadow-modal)] dark:border-white/10 dark:bg-[#1a1c25] dark:text-white">
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search jurisdictions..."
              className="mb-2"
            />
            <div className="max-h-56 overflow-y-auto space-y-1">
              {filteredJurisdictions.length === 0 && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No jurisdiction found.
                </div>
              )}
              {filteredJurisdictions.map(jurisdiction => (
                <button
                  key={jurisdiction}
                  type="button"
                  onClick={() => {
                    onChange(jurisdiction)
                    setIsDropdownOpen(false)
                    setSearchValue('')
                  }}
                  className={`w-full rounded-sm px-2 py-1.5 text-left text-sm text-[#101828] hover:bg-muted dark:text-inherit dark:hover:bg-white/10 ${
                    value === jurisdiction ? 'bg-muted dark:bg-white/10' : ''
                  }`}
                >
                  {jurisdiction}
                </button>
              ))}
            </div>
          </div>
        )}
        </div>
        <IconButton
          type="button"
          size="lg"
          className="shrink-0"
          onClick={() => setShowAddForm(!showAddForm)}
          title="Add jurisdiction"
          aria-label="Add jurisdiction"
        >
          <Plus className="h-4 w-4" />
        </IconButton>
      </div>

      {showAddForm && (
        <div className="mt-3 rounded-lg border border-[var(--outline-border)] bg-[var(--surface-sunken)] p-3 dark:border-indigo-300/20 dark:bg-slate-950/35">
          <Input
            value={newJurisdiction}
            onChange={(e) => setNewJurisdiction(e.target.value)}
            placeholder="Enter custom jurisdiction (e.g., 'Canada (Ontario)')"
            className="mb-2"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAdd()
              }
            }}
          />
          <div className="flex gap-2">
            <Button type="button" variant="accent" size="xs" onClick={handleAdd} className="flex-1">
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => { setShowAddForm(false); setNewJurisdiction('') }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {savedJurisdictions.length > 0 && (
        <div className="mt-2">
          <div className="mb-1.5 text-xs text-[var(--lm-muted)] dark:text-slate-400">Saved Jurisdictions:</div>
          <div className="flex flex-wrap">
            {savedJurisdictions.map(j => (
              <CustomDataTag
                key={j}
                label={j}
                onRemove={() => handleRemove(j)}
                colorClass="blue"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
